"use client";

import { useEffect, useState, useCallback } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useMessaging } from "@/contexts/messaging-context";
import { ConversationListItem } from "./ConversationListItem";
import { ConversationThread } from "./ConversationThread";
import { ContactsList } from "./ContactsList";
import { MessageSearch } from "./MessageSearch";
import { USLandModal, ToolbarButtonProps } from "@/components/usland-modal/usland-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MessageSquarePlus,
  Search,
  Expand,
  ArrowLeft,
  Settings,
  Filter,
  MessageSquare,
  Users
} from "lucide-react";
import { getPusherClient } from "@/lib/pusher-client";
import { createOrGetConversation, getFavoriteContactIds } from "@/app/actions/messaging";
import { toast } from "sonner";

interface Conversation {
  id: string;
  lastMessageAt: Date | null;
  lastMessagePreview: string | null;
  otherUser: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  };
  listing: {
    id: string;
    title: string;
    address: string;
    price: number;
    images: string[];
  } | null;
  bugReport: {
    id: string;
    category: string;
    priority: string;
    status: string;
    url: string;
    description: string;
  } | null;
  unreadCount: number;
}

export function MessagesModal() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const {
    isModalOpen,
    closeModal,
    activeConversationId,
    setActiveConversation,
    activeListingId,
    conversations: contextConversations,
    setConversations
  } = useMessaging();

  const [conversations, setLocalConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileThread, setShowMobileThread] = useState(false);
  const [activeTab, setActiveTab] = useState<"conversations" | "contacts">("conversations");
  const [favoriteContactIds, setFavoriteContactIds] = useState<string[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Fetch conversations and favorites
  useEffect(() => {
    if (isModalOpen && status === "authenticated") {
      fetchConversations();
      fetchFavorites();
    }
  }, [isModalOpen, status]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (status === "authenticated" && session?.user?.id && isModalOpen) {
      const pusher = getPusherClient();
      const channel = pusher.subscribe(`user-${session.user.id}`);

      channel.bind("conversation-updated", () => {
        fetchConversations();
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
      };
    }
  }, [session, status, isModalOpen]);

  // Handle listing-based conversation creation
  useEffect(() => {
    if (isModalOpen && activeListingId && status === "authenticated") {
      // If opened with a listing ID, we might need to create/get a conversation
      handleListingConversation();
    }
  }, [isModalOpen, activeListingId, status]);

  async function fetchConversations() {
    try {
      const response = await fetch("/api/conversations");
      if (response.ok) {
        const data = await response.json();
        setLocalConversations(data);
        setConversations(data); // Update context

        // Auto-select first conversation if none selected
        if (!activeConversationId && data.length > 0) {
          setActiveConversation(data[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  }

  async function fetchFavorites() {
    try {
      const result = await getFavoriteContactIds();
      if (result.error) {
        console.error("Failed to fetch favorites:", result.error);
      } else {
        setFavoriteContactIds(result.favoriteIds);
      }
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
    }
  }

  async function handleListingConversation() {
    // This would be called when opening from ContactSellerButton
    // Implementation depends on how the listing context is passed
  }

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.otherUser.name?.toLowerCase().includes(query) ||
      conv.listing?.title.toLowerCase().includes(query) ||
      conv.lastMessagePreview?.toLowerCase().includes(query)
    );
  });

  const selectedConversation = conversations.find(
    c => c.id === activeConversationId
  );

  // Handle starting a new conversation from Contacts tab
  const handleStartConversation = useCallback(async (conversationId: string) => {
    // Switch to conversations tab
    setActiveTab("conversations");

    // Refresh conversations list
    await fetchConversations();

    // Select the new conversation
    setActiveConversation(conversationId);
  }, [setActiveConversation]);

  // Handle conversation deletion
  const handleConversationDelete = useCallback(async () => {
    // Refresh conversations list
    await fetchConversations();

    // If the deleted conversation was active, clear selection
    setActiveConversation(null);
  }, [setActiveConversation]);

  // Handle favorite change
  const handleFavoriteChange = useCallback(async () => {
    // Refresh favorites list
    await fetchFavorites();
  }, []);

  // Toolbar buttons
  const toolbarButtonsLeft: ToolbarButtonProps[] = [
    {
      icon: activeTab === "conversations" ? <Users className="h-4 w-4 text-slate-900 dark:text-cyan-400" /> : <MessageSquare className="h-4 w-4 text-slate-900 dark:text-cyan-400" />,
      label: activeTab === "conversations" ? "Find People" : "Conversations",
      onClick: () => {
        setActiveTab(activeTab === "conversations" ? "contacts" : "conversations");
      },
    },
    {
      icon: <Search className="h-4 w-4 text-slate-900 dark:text-cyan-400" />,
      label: "Search",
      onClick: () => {
        setIsSearchMode(!isSearchMode);
      },
    },
  ];

  const toolbarButtonsRight: ToolbarButtonProps[] = [
    {
      icon: <Expand className="h-4 w-4 text-slate-900 dark:text-cyan-400" />,
      label: "Expand to Page",
      onClick: () => {
        closeModal();
        router.push("/messages" + (activeConversationId ? `/${activeConversationId}` : ""));
      },
    },
  ];

  // Mobile back button for thread view
  const handleMobileBack = () => {
    setShowMobileThread(false);
    setActiveConversation(null);
  };

  return (
    <USLandModal
      open={isModalOpen}
      onOpenChange={closeModal}
      title="Messages"
      toolbarButtonsLeft={toolbarButtonsLeft}
      toolbarButtonsRight={toolbarButtonsRight}
      className={cn(
        "max-w-6xl w-[95vw] h-[85vh]",
        "bg-card dark:bg-gray-900/85 backdrop-blur-2xl",
        "border border-gray-200 dark:border-cyan-500/25",
        "shadow-[0_0_60px_rgba(6,182,212,0.2),0_20px_60px_rgba(0,0,0,0.5)]",
        "ring-1 ring-gray-200 dark:ring-cyan-500/10"
      )}
      contentClassName="p-0"
      titleBarClassName={cn(
        "bg-gradient-to-r from-gray-100 via-gray-100/95 to-gray-200/90 dark:from-gray-900/80 dark:via-gray-900/70 dark:to-gray-800/80",
        "backdrop-blur-xl border-b border-gray-200 dark:border-cyan-500/20"
      )}
    >
      {/* Non-blocking backdrop overlay style */}
      <style jsx global>{`
        [role="dialog"]:has(.messages-modal-content) ~ [data-radix-portal] {
          pointer-events: none;
        }
        [role="dialog"]:has(.messages-modal-content) {
          pointer-events: auto;
        }
      `}</style>

      <div className="messages-modal-content flex h-full">
        {/* Desktop Layout - Two Columns */}
        <div className="hidden md:flex w-full h-full">
          {/* Left Column - Tabs with Conversations and Contacts (40%) with glass effect */}
          <div className={cn(
            "w-[40%] flex flex-col",
            "border-r border-gray-200 dark:border-cyan-500/20",
            "bg-card dark:bg-gray-900/85   backdrop-blur-sm",
            "relative"
          )}>
            {/* Vertical glow separator */}
            <div className="absolute top-0 right-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gray-300 dark:via-cyan-400/40 to-transparent" />

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "conversations" | "contacts")} className="flex flex-col h-full">
              {/* Tabs Header */}
              <div className="p-2 border-b border-gray-200 dark:border-cyan-500/20 bg-gray-100 dark:bg-gray-900/60   backdrop-blur-sm">
                <TabsList className="grid w-full grid-cols-2 bg-gray-200 dark:bg-gray-800/60  ">
                  <TabsTrigger value="conversations" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-600 dark:data-[state=active]:bg-cyan-500/20 dark:data-[state=active]:text-cyan-400">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Conversations
                  </TabsTrigger>
                  <TabsTrigger value="contacts" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-600 dark:data-[state=active]:bg-cyan-500/20 dark:data-[state=active]:text-cyan-400">
                    <Users className="h-4 w-4 mr-2" />
                    Contacts
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Conversations Tab */}
              <TabsContent value="conversations" className="flex-1 flex flex-col m-0">
                {isSearchMode ? (
                  <MessageSearch
                    onResultClick={(conversationId) => {
                      setActiveConversation(conversationId);
                      setShowMobileThread(true);
                    }}
                    onClose={() => setIsSearchMode(false)}
                  />
                ) : (
                  <>
                    {/* Search Bar */}
                    <div className="p-4 border-b border-gray-200 dark:border-cyan-500/20 bg-gray-100 dark:bg-gray-900/60  backdrop-blur-sm">
                      <Input
                        id="message-search"
                        type="search"
                        placeholder="Search conversations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={cn(
                          "bg-gray-200 dark:bg-gray-800/60 border-border/15",
                          "focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20",
                          "placeholder:text-gray-500 text-foreground",
                          "transition-[border-color,box-shadow] duration-200"
                        )}
                      />
                    </div>

                    {/* Conversations List */}
                    <ScrollArea className="flex-1">
                      <div className="p-4 space-y-2">
                        {loading ? (
                          <>
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="p-3 animate-pulse">
                                <div className="flex items-start gap-3">
                                  <Skeleton className="h-10 w-10 rounded-full bg-gray-800/50" />
                                  <div className="flex-1 space-y-2">
                                    <Skeleton className="h-4 w-32 bg-gray-800/50" />
                                    <Skeleton className="h-3 w-full bg-gray-800/50" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        ) : filteredConversations.length === 0 ? (
                          <div className="text-center py-16">
                            <div className="relative inline-block">
                              <MessageSquare className="mx-auto h-14 w-14 text-muted-foreground opacity-50" />
                              <div className="absolute inset-0 h-14 w-14 mx-auto rounded-full bg-blue-500/5 dark:bg-cyan-500/5 animate-ping" />
                            </div>
                            <h3 className="mt-6 text-sm font-semibold text-muted-foreground">
                              {searchQuery ? "No matching conversations" : "No messages yet"}
                            </h3>
                            <p className="mt-2 text-xs text-muted-foreground max-w-xs mx-auto">
                              {searchQuery
                                ? "Try a different search term"
                                : "Start a conversation by finding people in the Contacts tab"}
                            </p>
                          </div>
                        ) : (
                          filteredConversations.map((conversation, index) => (
                            <div
                              key={conversation.id}
                              className="animate-fade-in"
                              style={{ animationDelay: `${index * 50}ms` }}
                            >
                              <ConversationListItem
                                conversation={conversation}
                                isActive={conversation.id === activeConversationId}
                                isFavorite={favoriteContactIds.includes(conversation.otherUser.id)}
                                onClick={() => setActiveConversation(conversation.id)}
                                onDelete={handleConversationDelete}
                                onFavoriteChange={handleFavoriteChange}
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </>
                )}
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts" className="flex-1 m-0">
                <ContactsList onStartConversation={handleStartConversation} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Active Thread (60%) with lighter background */}
          <div className="flex-1 flex flex-col bg-gray-50/50 dark:bg-gray-900/20 backdrop-blur-sm relative">
            {/* Subtle left glow line */}
            <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-transparent via-gray-300 dark:via-cyan-400/20 to-transparent" />

            {selectedConversation ? (
              <div className="animate-fade-in">
                <ConversationThread
                  conversationId={selectedConversation.id}
                  otherUser={selectedConversation.otherUser}
                  listing={selectedConversation.listing}
                  bugReport={selectedConversation.bugReport}
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center animate-fade-in">
                  <div className="relative inline-block mb-6">
                    <MessageSquare className="mx-auto h-20 w-20 text-muted-foreground opacity-40" />
                    <div className="absolute inset-0 h-20 w-20 mx-auto rounded-full bg-blue-500/10 dark:bg-cyan-500/10 blur-2xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                    Select a conversation
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                    Choose from your existing conversations or start a new one
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout - Single Column with Navigation */}
        <div className="md:hidden flex flex-col w-full h-full">
          {!showMobileThread ? (
            <>
              {/* Mobile Conversation List */}
              <div className="flex-1 flex flex-col animate-slide-in-right">
                {/* Search Bar */}
                <div className="p-3 border-b border-gray-200 dark:border-cyan-500/20 bg-gray-100 dark:bg-gray-900/60 backdrop-blur-sm">
                  <Input
                    type="search"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={cn(
                      "bg-gray-200 dark:bg-gray-800 border-border/15",
                      "focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20",
                      "placeholder:text-gray-500 text-sm text-foreground",
                      "transition-[border-color,box-shadow] duration-200"
                    )}
                  />
                </div>

                {/* Conversations List */}
                <ScrollArea className="flex-1">
                  <div className="p-3 space-y-2">
                    {loading ? (
                      <>
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="p-3">
                            <div className="flex items-start gap-3">
                              <Skeleton className="h-10 w-10 rounded-full" />
                              <div className="flex-1 space-y-2">
                                <Skeleton className="h-4 w-32" />
                                <Skeleton className="h-3 w-full" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : filteredConversations.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageSquare className="mx-auto h-10 w-10 text-muted-foreground" />
                        <h3 className="mt-3 text-sm font-semibold text-muted-foreground">
                          No messages
                        </h3>
                      </div>
                    ) : (
                      filteredConversations.map((conversation) => (
                        <ConversationListItem
                          key={conversation.id}
                          conversation={conversation}
                          isActive={false}
                          isFavorite={favoriteContactIds.includes(conversation.otherUser.id)}
                          onClick={() => {
                            setActiveConversation(conversation.id);
                            setShowMobileThread(true);
                          }}
                          onDelete={handleConversationDelete}
                          onFavoriteChange={handleFavoriteChange}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            </>
          ) : (
            <>
              {/* Mobile Thread View */}
              <div className="flex-1 flex flex-col animate-slide-in-right">
                {/* Mobile Thread Header with enhanced styling */}
                <div className={cn(
                  "flex items-center gap-3 p-3",
                  "border-b border-gray-200 dark:border-cyan-500/20",
                  "bg-gradient-to-r from-gray-100/80 via-gray-100/70 to-gray-200/80 dark:from-gray-900/80 dark:via-gray-900/70 dark:to-gray-800/80",
                  "backdrop-blur-sm"
                )}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleMobileBack}
                    className={cn(
                      "h-8 w-8",
                      "hover:bg-gray-100 dark:hover:bg-cyan-500/10 hover:text-blue-600 dark:hover:text-cyan-400",
                      "transition-[background-color,color] duration-200"
                    )}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-foreground">
                      {selectedConversation?.otherUser.name || "User"}
                    </h3>
                  </div>
                </div>

                {/* Thread Content */}
                {selectedConversation && (
                  <div className="flex-1 animate-fade-in">
                    <ConversationThread
                      conversationId={selectedConversation.id}
                      otherUser={selectedConversation.otherUser}
                      listing={selectedConversation.listing}
                      bugReport={selectedConversation.bugReport}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </USLandModal>
  );
}